import type { ReactNode } from 'react';
import { cn } from '../../utils';

export interface ErrorStateProps {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  error?: Error | null;
  onRetry?: () => void;
}

/** Generic error card — used inside DataTable and standalone. */
export function ErrorState({
  title = 'Something went wrong',
  description,
  action,
  className,
  error,
  onRetry,
}: ErrorStateProps) {
  const resolvedDescription = description ?? error?.message;
  const resolvedAction =
    action ??
    (onRetry ? (
      <button
        type="button"
        className="rounded-[4px] border border-[var(--color-border)] px-3 py-1.5 text-[12px] text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
        onClick={onRetry}
      >
        Retry
      </button>
    ) : null);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-10 text-center',
        'text-[var(--color-text-secondary)]',
        className,
      )}
      role="alert"
    >
      <div className="space-y-1">
        <p className="text-[15px] font-medium text-[var(--color-error)]">{title}</p>
        {resolvedDescription ? (
          <p className="max-w-[480px] text-[13px] text-[var(--color-text-secondary)]">
            {resolvedDescription}
          </p>
        ) : null}
      </div>
      {resolvedAction ? <div className="pt-1">{resolvedAction}</div> : null}
    </div>
  );
}
